import React, { useState, useEffect } from "react";
import axios from "axios";
import { format, differenceInWeeks } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFeather,
  faDna,
  faCalendarAlt,
  faClipboardList,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import { useShoppingContext } from "../../../../context/ShoppingContext";
import AddGalponForm from "../AddGalponForm/AddGalponForm";
import LabelWithTooltip from "../../../Components/LabelWothTooltip/LabelWithTooltip";
import "./AddAveForm.css";
import { useAuth } from "../../../../context/AuthContext";

const AddAveForm = () => {
  const {
    setAves,
    galpones,
    setShowAddAves,
    fetchMisAves,
    granjaActiva,
    setShowAddGalpon,
    showAddGalpon,
    setLoading,
    fetchMisGalpones,
    fetchMisGranjas,
    selectedGalpon,
    setSelectedGalpon,
    renderDateTitle,
    nuevoGalpon,
    setGalponCreatedFromAveForm,
    aves,
  } = useShoppingContext();

  const { authenticatedUser } = useAuth();

  const [formData, setFormData] = useState({
    nombre: "",
    especie: "",
    cantidad_inicial: "",
    fecha_recepcion: format(new Date(), "yyyy-MM-dd"),
    edad_llegada: "",
    edad_actual: "",
    saldo: "",
    galponId: selectedGalpon || "",
    granjaId: granjaActiva,
    id_alimento: 0,
    ingesta_diaria: 0,
    userId: authenticatedUser.id,
  });

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [recienNacidas, setRecienNacidas] = useState(true); // Checkbox estado

  useEffect(() => {
    fetchMisGranjas();
    fetchMisGalpones();
    fetchMisAves();
  }, [fetchMisGranjas, fetchMisGalpones, fetchMisAves]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      saldo: prevData.cantidad_inicial,
    }));
  }, [formData.cantidad_inicial]);

  useEffect(() => {
    if (nuevoGalpon) {
      setFormData((prevData) => ({
        ...prevData,
        galponId: nuevoGalpon.id,
      }));
    }
  }, [nuevoGalpon]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  // Calcular edad actual automáticamente
  const calculateEdadActual = (fechaRecepcion) => {
    const today = new Date();
    const recepcionDate = new Date(fechaRecepcion);
    const edadEnSemanas = differenceInWeeks(today, recepcionDate);
    return edadEnSemanas < 0 ? 0 : edadEnSemanas; // Evitar edades negativas
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    const edadCalculada = calculateEdadActual(value);

    setFormData((prevData) => ({
      ...prevData,
      fecha_recepcion: value,
      edad_actual: edadCalculada,
    }));
  };

  const handleCheckboxChange = () => {
    setRecienNacidas(!recienNacidas);
    if (!recienNacidas) {
      setFormData((prevData) => ({
        ...prevData,
        edad_llegada: 0, // Si llegaron recién nacidas, la edad es 0
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        edad_llegada: "", // Si no son recién nacidas, permitir que el usuario ingrese la edad
      }));
    }
  };

  const onAddAve = (ave) => {
    setAves((prevAves) => [...prevAves, ave]);
  };

  const validateStep2 = () => {
    let formErrors = {};

    if (!formData.galponId) {
      formErrors.galponId = "Falta seleccionar un Galpón.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const validateStep3 = () => {
    let formErrors = {};

    if (!formData.nombre.trim()) {
      formErrors.nombre = "Falta ingresar el nombre del lote.";
    }
    if (!formData.especie.trim()) {
      formErrors.especie = "Falta ingresar la genética.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const validateStep4 = () => {
    let formErrors = {};

    if (!formData.cantidad_inicial) {
      formErrors.cantidad_inicial = "Falta ingresar la cantidad inicial.";
    }

    if (!formData.fecha_recepcion) {
      formErrors.fecha_recepcion = "Falta seleccionar la fecha de recepción.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const nextStep = () => {
    let isValid = false;
    if (currentStep === 2) {
      isValid = validateStep2();
    } else if (currentStep === 3) {
      isValid = validateStep3();
    } else if (currentStep === 4) {
      isValid = validateStep4();
    }

    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    if (currentStep === 2 || currentStep === 1) {
      setSelectedGalpon(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading("agregando nuevo lote");
    setShowAddAves(false);
    axios
      .post(`${process.env.REACT_APP_API_URL}/aves`, formData)
      .then((response) => {
        onAddAve(response.data.ave);
        resetForm();
        fetchMisAves();
        setLoading(false);
        setSelectedGalpon(null);
      })
      .catch((error) => {
        console.error("Error al agregar el ave:", error);
        setErrors((prevErrors) => ({
          ...prevErrors,
          submit: "Error al agregar el lote de aves. Inténtalo de nuevo.",
        }));
        setLoading(false);
      });
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      especie: "",
      cantidad_inicial: "",
      fecha_recepcion: format(new Date(), "yyyy-MM-dd"),
      edad_llegada: "",
      edad_actual: "",
      saldo: "",
      galponId: selectedGalpon || "",
      granjaId: granjaActiva,
    });
    setRecienNacidas(false); // Resetear checkbox
  };

  // Definir descripciones para campos específicos
  const fieldDescriptions = {
    galponId: "Seleccione el galpón donde vivirán las aves o cree uno nuevo.",
    especie: "Ingrese la genética de las aves (e.g., Lohmann Brown, Hisex).",
    nombre: "Ingrese un nombre para identificar el Lote de aves.",
    // Otros campos con descripciones pueden agregarse aquí
  };

  const handleCreateGalpon = () => {
    setShowAddGalpon(true); // Muestra el formulario para crear nuevo galpón
    setGalponCreatedFromAveForm(true); // Indica que el galpón se crea desde AddAveForm
  };

  useEffect(() => {
    if (selectedGalpon && currentStep === 1) {
      setCurrentStep(2); // Si se selecciona o crea un galpón, avanza al siguiente paso automáticamente
    }
  }, [selectedGalpon, currentStep]);

  return (
    <div className="add-form-container">
      <form onSubmit={handleSubmit} className="add-ave-form">
        {/* Paso 1: Selección de Galpón */}
        {currentStep === 1 && (
          <div className="step1-container">
            <h2>¿Cómo deseas continuar?</h2>
            <div className="button-group">
              {galpones.length > 0 && (
                <button
                  type="button"
                  className="btn btn-green"
                  onClick={() => setCurrentStep(2)} // Selecciona galpón existente
                >
                  Seleccionar Galpón Existente
                </button>
              )}

              <button
                type="button"
                className="btn btn-green"
                onClick={handleCreateGalpon} // Crear nuevo galpón
              >
                Construir Nuevo Galpón
              </button>
              <div
                type="button"
                onClick={() => setShowAddAves(false)}
                className="btn btn-green-outline"
              >
                Regresar
              </div>
            </div>
          </div>
        )}

        {/* Paso 2: Selección de Galpón existente */}
        {currentStep === 2 && (
          <div className="step2-container">
            <h2 className="add-ave-form-title">Seleccionar Galpón Existente</h2>
            {/* Selección de galpones con el número de aves */}
            <div className="add-ave-form-group">
              <label htmlFor="galponId" className="add-galpon-form-label">
                <FontAwesomeIcon icon={faWarehouse} /> Galpón
              </label>
              <select
                id="galponId"
                name="galponId"
                value={formData.galponId}
                onChange={handleChange}
                className="add-galpon-form-input"
                required
              >
                <option value="">Seleccionar Galpón Existente</option>
                {galpones.map((galpon) => {
                  const avesEnGalpon = aves.filter(
                    (ave) => ave.galponId === galpon.id
                  );

                  // Sumar el saldo de todas las aves en el galpón
                  const totalAves = avesEnGalpon.reduce(
                    (acc, ave) => acc + ave.saldo,
                    0
                  );

                  return (
                    <option key={galpon.id} value={galpon.id}>
                      {`${galpon.nombre} (${totalAves} aves)`}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="add-ave-form-buttons">
              <div
                type="button"
                onClick={prevStep}
                className="btn btn-green-outline"
              >
                Volver
              </div>
              <div type="button" onClick={nextStep} className="btn btn-green">
                Siguiente
              </div>
            </div>
          </div>
        )}

        {/* Paso 3: Información del lote */}
        {currentStep === 3 && (
          <>
            <h2 className="add-ave-form-title">Información del Lote</h2>
            <div className="add-ave-form-group">
              <LabelWithTooltip
                htmlFor="nombre"
                description={fieldDescriptions["nombre"]}
              >
                <FontAwesomeIcon icon={faClipboardList} /> Nombre del Lote
              </LabelWithTooltip>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                className="add-ave-form-input"
                required
              />
              {errors.nombre && <p className="error-text">{errors.nombre}</p>}
            </div>
            <div className="add-ave-form-group">
              <LabelWithTooltip
                htmlFor="especie"
                description={fieldDescriptions["especie"]}
              >
                <FontAwesomeIcon icon={faDna} /> Genética
              </LabelWithTooltip>
              <input
                type="text"
                id="especie"
                name="especie"
                value={formData.especie}
                onChange={handleChange}
                className="add-ave-form-input"
                required
              />
              {errors.especie && <p className="error-text">{errors.especie}</p>}
            </div>

            <div className="add-ave-form-buttons">
              <div
                type="button"
                onClick={prevStep}
                className="btn btn-green-outline"
              >
                Volver
              </div>
              <div type="button" onClick={nextStep} className="btn btn-green">
                Siguiente
              </div>
            </div>
          </>
        )}

        {/* Paso 4: Información de Recepción */}
        {currentStep === 4 && (
          <>
            <h2 className="add-ave-form-title">Información de Recepción</h2>
            <div className="add-ave-form-group">
              <LabelWithTooltip
                htmlFor="cantidad_inicial"
                description={fieldDescriptions["cantidad_inicial"]}
              >
                <FontAwesomeIcon icon={faFeather} /> Cantidad Inicial de Aves
              </LabelWithTooltip>
              <input
                type="number"
                id="cantidad_inicial"
                name="cantidad_inicial"
                value={formData.cantidad_inicial}
                onChange={handleChange}
                className="add-ave-form-input"
                inputMode="numeric"
                required
              />
              {errors.cantidad_inicial && (
                <p className="error-text">{errors.cantidad_inicial}</p>
              )}
            </div>
            <div className="add-ave-form-group">
              <LabelWithTooltip
                htmlFor="fecha_recepcion"
                description={fieldDescriptions["especie"]}
              >
                <FontAwesomeIcon icon={faCalendarAlt} /> Fecha de Recepción
              </LabelWithTooltip>
              <input
                type="date"
                id="fecha_recepcion"
                name="fecha_recepcion"
                value={formData.fecha_recepcion}
                onChange={handleDateChange}
                className="add-ave-form-input"
                max={format(new Date(), "yyyy-MM-dd")}
                required
              />
              {errors.fecha_recepcion && (
                <p className="error-text">{errors.fecha_recepcion}</p>
              )}
            </div>

            {/* Checkbox para recien nacidas */}
            <div className="add-ave-form-group">
              <input
                type="checkbox"
                id="recienNacidas"
                checked={recienNacidas}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="recienNacidas" className="add-ave-form-label">
                ¿Llegaron recién nacidas?
              </label>
            </div>

            {/* Input para la edad de llegada si no son recién nacidas */}
            {!recienNacidas && (
              <div className="add-ave-form-group">
                <LabelWithTooltip htmlFor="edad_llegada">
                  Edad de Llegada (en semanas)
                </LabelWithTooltip>
                <input
                  type="number"
                  id="edad_llegada"
                  name="edad_llegada"
                  value={formData.edad_llegada}
                  onChange={handleChange}
                  className="add-ave-form-input"
                />
                {errors.edad_llegada && (
                  <p className="error-text">{errors.edad_llegada}</p>
                )}
              </div>
            )}

            <div className="add-ave-form-buttons">
              <div
                type="button"
                onClick={prevStep}
                className="btn btn-green-outline"
              >
                Volver
              </div>
              <div type="button" onClick={nextStep} className="btn btn-green">
                Siguiente
              </div>
            </div>
          </>
        )}

        {/* Paso 5: Confirmación de datos */}
        {currentStep === 5 && (
          <>
            <h2 className="add-ave-form-title">Agregar {formData.nombre}</h2>
            <ul className="add-ave-form-group">
              <li>
                <b>Genética: </b>
                {formData.especie}
              </li>
              <li>
                <b>Tamaño: </b>
                {formData.saldo} aves
              </li>
              <li>
                <b>Fecha de Recepción: </b>
                {renderDateTitle(formData.fecha_recepcion)}
              </li>
              <li>
                <b>Edad actual: </b>
                {formData.edad_actual} semanas
              </li>
              {/* Otros datos de confirmación */}
            </ul>
            <div className="add-ave-form-buttons">
              <div
                type="button"
                onClick={prevStep}
                className="btn btn-green-outline"
              >
                Volver
              </div>
              <button type="submit" className="btn btn-green submit">
                Agregar
              </button>
            </div>
          </>
        )}
      </form>
      {showAddGalpon && <AddGalponForm />}
    </div>
  );
};

export default AddAveForm;
